/**
 * Every user-facing string in catalog setup (SB-2, Section A) and in the
 * locations manager.
 *
 * THE MANAGER IS NOT IN THE SAME PLACE ON BOTH CLIENTS SINCE W1 T2, which is
 * why this line no longer names one. iOS keeps it under Settings
 * (`Features/Settings/LocationsView.swift`), and web moved it into the session
 * builder on the user's own instruction. The STRINGS are unchanged and still
 * shared, which is the only thing this deck was ever about; the Swift twin's
 * docstring still says Settings because on iOS that is still true.
 *
 * A SECOND deck, deliberately, rather than more entries in `firstRun.ts`
 * (decision D-2). Both first-run decks scope themselves by their own docstrings
 * to invite, first-run, priming and account strings, and Section A's copy is
 * none of those. Widening a guard's remit past what the people who set that
 * remit agreed to is how a guard stops meaning anything.
 *
 * Its iOS twin is
 * `ios/GroundworkKit/Sources/GroundworkKit/CatalogCopy.swift` (`enum
 * CatalogCopy`), and `python3 scripts/check_copy_decks.py` compares the two on
 * every symbol they share. **A string added here belongs there too.** The
 * clients only "say the same words" -- which is the whole of what makes
 * "completes on iOS alone, and on web alone" one product rather than two -- if
 * something mechanical checks it.
 *
 * Placeholders are `{name}`-style and substituted with `fill()` from
 * `./firstRun`. English only; German is produced by the coach BACKEND as
 * message content, never as UI chrome.
 *
 * **Em dashes are amended out, and that is a ruling rather than a slip**
 * (D-3, confirmed at Gate A). PRODUCT.md:47 bans them anywhere in UI copy and
 * the SB-2 artboard used five. The wording is the artboard's; only the
 * punctuation moved. `catalog.test.ts` asserts the ban over this whole deck,
 * which is why the amendment had to land here rather than in a component.
 */
export const CATALOG_COPY = {
  // ── Step eyebrows ──
  // Templates rather than assembled fragments: the middot spacing and the
  // "N of M selected" order are copy decisions, and a component that
  // concatenates them makes each client's spacing its own private choice.
  stepOneEyebrow: 'Step 1 of 3 · Your gym',
  stepTwoEyebrow: 'Step 2 of 3 · {location}',
  stepThreeEyebrow: 'Step 3 of 3 · {brand} · {selected} of {total} selected',
  // The trim group is EquipmentGrid with a second source (D-5), so it takes
  // the same header shape with the step number dropped: it is a detour off
  // step 2, not a fourth step, and numbering it would say otherwise.
  standardEyebrow: 'Standard equipment · {selected} of {total} selected',

  // ── Step 1, the location ──
  locationTitle: 'Where do you train?',
  locationBody:
    "Your equipment lives at a location. Sessions are built from what's actually standing there.",
  locationFieldLabel: 'Gym name',
  locationFieldPlaceholder: 'FitPark Mitte',
  // The line that makes a single-location default honest rather than a
  // limitation. Without it, step 1 reads as "you get one gym".
  locationHelper: 'You can add more gyms later',
  locationCreating: 'Creating…',
  // A create that fails says so under the field, on both clients (Apple design review X-10). iOS used
  // to say nothing there (the button simply returned from Creating… to Next); the owner ruled both say
  // it, so this is a shared symbol.
  locationCreateFailed: 'Could not create this location. Try again.',

  // ── Actions ──
  // Both clients carry both labels so the pair stays inside the shared set the
  // parity check compares. Web renders `continueLabel`; iOS renders
  // `nextLabel`, per source doc §8.
  continueLabel: 'Continue',
  nextLabel: 'Next',
  doneLabel: 'Done',
  // Offered on the location step ONLY when catalog setup is entered from first
  // run (D-4). A user with zero locations is a legitimate state (SB-1 §2), so
  // the app must not be reachable only through a flow.
  skipForNow: 'Skip for now',
  savingLabel: 'Saving…',

  // ── Step 2, the brands ──
  brandsTitle: 'Which brands are on the floor?',
  // Was "Pick what you recognize — you can change this any time in settings."
  // The em dash went first. "in settings" went with W1, which moved the gyms
  // manager into the session builder on web while iOS keeps it under Settings.
  // A shared symbol must be byte-identical and the deck has NO per-platform
  // mechanism -- `composites` splits one sentence across parts, it does not
  // hold two texts -- so naming either surface would be false on the other.
  // The sentence names none and is true on both.
  brandsBody: 'Pick what you recognize. You can change this any time.',
  // Singular and plural, for the same reason D-15 added locationsSummaryBrand:
  // a brand holding exactly one machine renders "1 machines" otherwise. Found by
  // Phase C round 2 with a seeded one-machine probe brand -- invisible against
  // today's catalogue, which is a single brand of 479, and wrong the moment a
  // seeded brand lands.
  brandMachine: '{count} machine',
  brandMachines: '{count} machines',
  brandMachineSelected: '{count} machine · selected',
  brandMachinesSelected: '{count} machines · selected',
  // What the tile says once the gym holds some of the brand, and the answer to
  // the two things wrong with the four symbols above it on the web tile.
  //
  // It is derived from the DRAFT rather than from the tile's ticked state, so
  // it is right on the first frame -- and since W5 there IS no ticked state to
  // read on either client: web deleted its `selectedBrands` array, so the
  // sentence and the tile's lit state are now one derived number everywhere
  // (D-5). While the array existed it started empty on every entry and
  // `?location=<id>` enters Section A at step 2, so a gym holding 51 of
  // Technogym's 479 machines arrived with this sentence right and the tile
  // printing it dim.
  //
  // And it states the SELECTED count, which `brandMachinesSelected` does not:
  // that symbol fills `{count}` from the brand's total, so a picked Technogym
  // tile read "479 machines · selected", which reads as 479 selected. Web
  // renders this symbol in its place. iOS did the same in I1, reshaping
  // `BrandTileSummary.machines(_:selected:)` to `machines(selected:of:)` so a
  // tick can no longer be passed where a count belongs -- so that pair is now
  // rendered by NEITHER client. Both symbols stay in both decks regardless:
  // deleting a shared symbol is its own decision, not a tidy-up.
  //
  // Same words and same order as `stepThreeEyebrow`'s tail, deliberately: it is
  // the same fact about the same brand one step apart, and the app must not say
  // it two ways. No singular/plural pair, unlike the four above -- "1 of 479
  // selected" is grammatical, so the branch those need does not exist here.
  brandMachinesSelectedOfTotal: '{selected} of {total} selected',

  // ── The pinned standard strip (D2, clause (c) RETIRED cycle 006) ──
  // It NAMES the kit and states the live count through placeholders. It makes
  // no claim about a past act, which is the inversion: this block used to say
  // it STATED the preselection rather than hiding it, and that is exactly what
  // went false. The client never witnessed the seeding, so "already in" and
  // "We preselected them" narrated something it cannot know still holds -- six
  // of ten live locations hold none of the twelve. The count is computed at
  // render from the same source the group reads, so the sentence cannot go
  // false at any population.
  // The title must keep naming the kit "Standard equipment": emptySearchBody
  // routes the user here BY THAT NAME, and both decks assert that literal, so
  // a headline rename would retire the referent of a sentence nothing in this
  // change touches.
  // Was 'Standard equipment, already in' / '... We preselected them; open the
  // group to trim.'
  standardStripTitle: 'Standard equipment',
  standardStripBody:
    "Dumbbells, barbells, racks, benches and bodyweight belong to every gym. {selected} of {total} are on this gym's list, and you can change that any time.",
  // A bare verb, no count: true at 0 and at 12 alike. It replaces
  // standardStripReviewAll and standardStripReviewSome, DELETED from both decks
  // in cycle 006 -- the count moved into the body, where a review label would
  // only restate the list the user is about to look at, and "Review N items"
  // has zero precedent across the ten screens the research pass read.
  standardStripOpen: "Choose what's there",

  // ── The trim group ──
  // Direction-neutral, and true at every count. It used to say "Trim what
  // isn't there" over "Untick anything your gym doesn't have", above twelve
  // tiles that may all be unticked: unperformable at zero, and false on six of
  // ten live locations. Fixing only the strip would have moved the same lie one
  // screen along.
  standardTrimTitle: "What's in this gym",
  standardTrimBody:
    'Dumbbells, barbells, racks, benches and bodyweight belong to every gym. Tick the ones yours has.',
  markAll: 'Mark all {count}',

  // ── Step 3, the equipment ──
  equipmentTitle: 'Pick your {brand} machines',
  equipmentSearchPlaceholder: 'Search {brand} equipment…',
  equipmentSearchLabel: 'Search {brand} equipment',
  selectedOnly: 'Selected only · {count}',

  // ── The filter row (W3 T1) ──
  //
  // User feedback, verbatim: "On the device picker we need filter, so i can
  // easier find devices." Both a search field and a selected-only toggle
  // already existed; neither was DISCOVERABLE. Search was gated on brand mode
  // and sat inline in a wrap row, and the toggle only appeared once something
  // was already selected, which is after the point it helps.
  //
  // The standard kit's own search strings, because the brand pair above is
  // brand-substituted and the twelve belong to no brand. Its own symbols rather
  // than a shared one for the reason `customSkipForNow` is its own symbol: two
  // decisions about two screens, and rewording one must not reword the other.
  standardSearchPlaceholder: 'Search standard equipment…',
  standardSearchLabel: 'Search standard equipment',
  // Accessible names for the two chip groups. Neither is drawn: the chips read
  // as their own vocabulary and a visible legend over each row is chrome the
  // references (Future Pro, SHEIN) do not carry. A group with no name at all is
  // a bare run of buttons to a screen reader, hence these.
  filterTypeLabel: 'Filter by type',
  filterMuscleLabel: 'Filter by muscle group',
  // The chip that CLEARS its group, and the shipping convention in both
  // references. It is the group's resting state, not an extra filter.
  filterAll: 'All',
  // Modality chips. Their words match `customModalityCardio` and friends and
  // their symbols do not, same rule as the search pair above -- those are a
  // form's options for one new item, these filter a catalogue.
  filterCardio: 'Cardio',
  filterStrength: 'Strength',
  // Declared for completeness of `Modality`, not because a chip is drawn today:
  // chips are built from the modalities the loaded items actually carry, and
  // neither source produces `mobility` (brand rows are cardio-or-strength by
  // construction, the twelve standard items are all strength). A modality that
  // appears without a label here would render an unlabelled chip.
  filterMobility: 'Mobility',
  // The six group names. `MUSCLE_LABELS` in `frontend/src/lib/muscles.ts` names
  // the 18 SLUGS; these name the folds over them, and the fold is a partition
  // asserted by `muscles.test.ts` so no slug is left matching no chip.
  muscleGroupChest: 'Chest',
  muscleGroupBack: 'Back',
  muscleGroupShoulders: 'Shoulders',
  muscleGroupArms: 'Arms',
  muscleGroupLegs: 'Legs',
  muscleGroupCore: 'Core',
  // The empty state a FILTER produces, as distinct from `emptySearchTitle`
  // below. That one is brand-substituted and routes out of the brand, which is
  // the right answer in brand mode and circular in the standard group (it
  // routes to Standard equipment, where the user already is). This pair names
  // the way back instead, and the way back always works.
  emptyFilterTitle: 'Nothing here matches',
  emptyFilterClear: 'Clear filters',

  addMissing: "+ Add something we're missing",
  // NAMES NO SURFACE, for the reason spelled out on `brandsBody` above: W1 moved
  // this flow's entry point into the builder on web and left it under Settings
  // on iOS, so a sentence naming either one is a lie on the other client.
  editableLater: 'Editable later',

  // ── Search with no results: it ROUTES rather than apologising ──
  // "Nothing matches" is a dead end; naming where the thing actually lives is
  // not. Kettlebells are named here and are deliberately among the twelve
  // standard items, so the sentence is not a dead end either.
  // Was "Try Standard equipment — kettlebells live there — or add it as custom."
  emptySearchTitle: 'Nothing in {brand} matches',
  emptySearchBody: 'Try Standard equipment (kettlebells live there), or add it as custom.',
  emptySearchAllBrands: 'Search all brands',
  emptySearchCustom: '+ Custom',

  // ── Saving failed ──
  // A FACTUAL CLAIM, and the draft is what keeps it: the unsaved set lives in
  // localStorage on web and in UserDefaults on iOS, cleared only on a
  // successful save, so Retry resubmits the same items. If that is ever
  // untrue, this sentence is a lie told on a gym floor. Rendered at all three
  // save sites (brand step, equipment step, trim group), never as a modal.
  saveFailedTitle: "Couldn't save your catalog",
  saveFailedBody: 'Your picks are kept on this device. Nothing was lost.',
  saveFailedRetry: 'Retry',
  saveFailedKeepEditing: 'Keep editing',

  // ── Custom equipment sheet ──
  customEyebrow: 'New equipment · {location}',
  customNameLabel: 'Name',
  customNamePlaceholder: 'Landmine Station',
  customModalityLegend: 'Modality',
  customModalityStrength: 'Strength',
  customModalityCardio: 'Cardio',
  customModalityMobility: 'Mobility',
  // AMENDED, and not only for the em dash (D-8, confirmed at Gate A). The
  // shipped sentence was "Snap it on the gym floor or leave it — a movement
  // glyph fills in. No empty image ever shows." There is no file input, no
  // camera control, no `image` field on CustomEquipmentIn, no upload route,
  // and `custom_equipment.image_path` is never written: only the second half
  // of that offer works. The photo path is its own spec. What is left is a
  // promise SB-7's tier 3 keeps.
  customPhotoNote:
    'Leave the photo for now. A movement glyph fills in, so no empty image ever shows.',
  customCancel: 'Cancel',
  customSubmit: 'Add to catalog',
  customSubmitting: 'Adding…',
  // Web only: a failed add or edit used to stop and say nothing (Apple design review X-10). iOS words
  // its own custom-equipment failures.
  customCreateFailed: 'Could not add this equipment. Try again.',
  customEditFailed: 'Could not save this change. Try again.',

  // ── The sheet's BLOCK ARM (SB-4 §7; moved into the deck cycle 015) ──
  //
  // Five strings web has rendered from inline literals since cycle 003. They
  // are here now because iOS gains the same arm in SB-4b slice 5, and
  // CLAUDE.md's rule is that a string on one client belongs in both decks --
  // `check_copy_decks.py` compares the INTERSECTION, so a string in neither
  // deck is invisible to it and the drift it would hide is the one nothing
  // even claims to check.
  //
  // **The words did not change in transit.** That is the specific failure
  // `check_copy_decks.py`'s own docstring records for `accountDeleteBody`:
  // two internally well-formed strings, each passing its own deck's tests,
  // saying different things on the screen where a decision is taken. Every
  // value below is the literal that stood at the call site.
  //
  // `customSkipForNow` repeats `skipForNow`'s words under its own symbol, the
  // same treatment `skipForNow` itself got from `frBandSkip`: the words match,
  // the symbol does not, so neither screen's remit stretches over the other's.
  // Catalog setup's step-1 skip and the block arm's skip are two decisions
  // about two different things, and one symbol would make a rewording of
  // either silently reword the other.
  customSkipForNow: 'Skip for now',
  customAddToBlock: 'Add to block',
  // The muscle fieldset's legend. A question, because the tagging is PROMPTED
  // and skippable -- not an instruction, which `Skip for now` would then read
  // as disobeying.
  customMusclesLegend: 'What does it train?',
  // STATED, not asked. The block owns the field set (SB-3), so a tracking-type
  // question here would be a second answer to a settled question. `{fields}`
  // is the block's own field labels, joined by the client; a block that tracks
  // nothing renders the word for an empty set, which is app chrome rather than
  // deck copy and stays at the call site.
  customFieldsInherited: 'Fields: {fields} (set by the block)',
  // The fold over the rest of the 18-slug vocabulary. `{count}` is computed at
  // render from the vocabulary itself, never written out here -- a literal 14
  // would go false the day a slug is added, in a deck no test of the
  // vocabulary can reach.
  customMusclesMore: '+ {count} more',

  // ── The movement control (cycle 013, AC-361 / 013-A27) ──
  //
  // LABEL ONLY. There is no helper sentence here and its absence is a ruling,
  // not an omission. Every invisible-effect field in two independent corpus
  // passes works by naming its consumer in the present tense -- Apple's alt
  // text, Notion, Revolut, Teachable, CVS -- and ours cannot: the only
  // consumer is a movement glyph that has not been drawn (R-1 paused the 23
  // assets). That leaves a false present tense, a forbidden future tense, or
  // no sentence. Hevy ships the third: `Primary Muscle Group` carries a label,
  // an optional marker and nothing else. AC-361 bans `icon`, `picture`,
  // `image`, `glyph`, `illustration`, `symbol`, `artwork` and any
  // forward-looking promise, so do not add one here when the assets land
  // either -- reopen 013-A27 first.
  customMovementLabel: 'Movement',
  // The empty option, and NOT `Select...`. The same control is reopened over
  // an existing value on the edit pass (AC-364), where an instruction reads
  // wrong: what the user needs there is the name of the state, not a nudge.
  // Choosing it and saving is how a movement is cleared, which is the PATCH
  // that sends an explicit `null` (013-A21).
  customMovementNone: 'Not set',

  // ── Editing one custom row's movement (013-A25, AC-364) ──
  customEditEyebrow: 'Edit equipment · {name}',
  customEditSubmit: 'Save',
  // The device row's overflow item. It edits the device the row already
  // names; it never changes WHICH device the row names -- see the comment
  // beside it in `DeviceRow.tsx`, and 012-A16, which removed a control that
  // did the second thing while claiming the first.
  customEditMovement: 'Edit movement',

  // ── The locations manager (Settings on iOS, the session builder on web
  //    since W1 T2 -- see this file's docstring) ──
  locationsEmpty: "No gyms yet. Add one and pick what's standing in it.",
  locationsSummaryBrands: '{devices} devices · {brands} brands',
  // The singular, because D-14 made ONE the common case rather than an edge
  // one: a gym with Technogym and nothing else is what most users have, and
  // until the count worked at all this string had never rendered. Without
  // this symbol day one reads '489 devices · 1 brands'. Clients branch three
  // ways: 0 -> ...Standard, 1 -> ...Brand, 2 or more -> ...Brands.
  locationsSummaryBrand: '{devices} devices · {brands} brand',
  // "standard only" is why toggling in the trim group must write
  // `source: 'standard'` rather than 'brand': this line reads that column.
  locationsSummaryStandard: '{devices} devices · standard only',
  locationsEditCatalog: 'Edit catalog',
  locationsAdd: '+ Add location',
  locationsAddSubmit: 'Add location',
  // RULED 2026-08-29 (René): "Starts empty" became untrue the moment S1 seeded
  // the standard kit server-side, because the row below this form immediately
  // reads "12 devices, standard only". The B1 pass raised it rather than
  // rewriting words nobody had reviewed -- a copy deck is exactly where a copy
  // change must be visible instead of smuggled in beside a punctuation fix.
  locationsNewHelper: 'Starts with the standard kit. Pick its machines next.',
  // The row's own non-destructive edit. `useRenameLocation` has existed in
  // `api/locations.ts` since SB-1 and nothing called it, so a gym typo'd at
  // creation could only be corrected by deleting it -- which the 409 below
  // refuses the moment one session points at the gym.
  locationsRename: 'Rename',
  // Bare, and the same word `customEditSubmit` uses: the editing state stands
  // in the row that names the gym, so the label has nothing left to repeat.
  // Its own symbol rather than a shared one, for the reason `customSkipForNow`
  // is its own symbol -- two decisions about two screens, so rewording one
  // must not silently reword the other.
  //
  // The editing arm's OTHER button reuses `customCancel`, and the asymmetry is
  // the point rather than an oversight. What is decided here is the affirmative
  // word: "Save" over "Save name" or "Rename", a judgement about what this row
  // has already said. Backing out is not a judgement about anything -- every
  // sheet in the app dismisses with the same word, none of them has a reason
  // to reword it alone, and the add form directly above reuses it too. A symbol
  // exists to hold a decision; splitting one that holds none buys two strings
  // to keep in step and no editorial freedom worth having.
  locationsRenameSubmit: 'Save',
  // Shaped on `locationsDeleteFailed` below, and deliberately not a status
  // code: the row and the draft are both still on screen and still theirs to
  // retry. It names no way forward, because unlike the 409 there is none to
  // name.
  locationsRenameFailed: 'Could not rename this location.',
  // The API answers 409 rather than orphaning the sessions, so the copy names
  // the way forward instead of reporting a conflict. Web only: iOS ships
  // without delete this cycle (D-6), because "move the sessions" has no iOS
  // surface until SB-5.
  locationsDelete: 'Delete',
  locationsDeleteBlocked: 'Sessions still use this location. Move them to another location first.',
  locationsDeleteFailed: 'Could not delete this location.',
} as const
